/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Page Subscriber Mobile component
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Misha Kokoulin
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Nikolay Sviridov
 * @version 1.36.1
 * @since 1.0.0
 */

import { Fragment, memo, useEffect, useLayoutEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { TableFilterPresetQueryId } from '@/main/components/Table/types/tableTypes';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { setLocalStorageDataAction } from '@/main/modules/storage/storageActions';
import { ELocalStorageKeys } from '@/main/modules/storage/storageTypes';
import { setIsFastActionPage, setIsFastBasketPage, setTableContext } from '@/main/modules/table/slice';
import { InfoBlock } from '@components/common';
import Title from '@components/common/Title/Title';
import { EColorStatus } from '@modules/app/appConstants';
import withAsyncStateManagement from '@modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectTranslationPageSubscriberMobile } from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { ONBOARDING_DATA_ATTR } from '@modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { initialise, useTableInitialise } from '@modules/table';
import { ETableButtonsMode } from '@modules/table/types';


import CorporatePagePortal from '../CorporatePage/CorporatePagePortal';
import { makeSelectFeatureEnabled } from '../PageAuth/selectors/authSelectors';
import { clearFilter } from '../PageSubscriberMobile/components/SubscriberMobileExtFilter/actions/extFilterApiActions';
import {
    SubscriberMobileModeStyled,
    SubscriberMobileTableStatusStyled,
} from './components/styled/PageSubscriberMobileStyled';
import TableSubscriber from './components/TableSubscriber';
import TableSubscriberTitle from './components/TableSubscriberTitle';
import { SUBSCRIBERS_IN_ROAMING, SUBSCRIBER_TABLE_CONFIG } from './constants';
import { useTableButtonsMode } from './hooks/useTableButtonsMode';
import { saga } from './saga';
import { useLazyGetTableStatusQuery } from './store';

interface IPageSubscriberMobileProps {
    isFastBasketPage?: boolean;
    isFastActionPage?: boolean;
    context?: string;
}

export const PageSubscriberMobile = ({ isFastBasketPage, isFastActionPage, context }: IPageSubscriberMobileProps) => {

    const dispatch = useDispatch();

    const pageTranslation = useSelector(makeSelectTranslationPageSubscriberMobile());
    const isMainOperationsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.MAIN_OPERATIONS));

    const [pollingInterval, setPollingInterval] = useState(1000);
    const { updatedConfig } = useTableInitialise(SUBSCRIBER_TABLE_CONFIG);

    const [getListStatus, listStatusResult] = useLazyGetTableStatusQuery({
        pollingInterval,
    });

    const { data: listStatusData, isSuccess: isListStatusFetchedSuccessfully } = listStatusResult;
    const isComplete = listStatusData?.isComplete;

    /* Mode для кнопок таблицы */
    const { tableBtnActive } = useTableButtonsMode();

    useLayoutEffect(() => {
        dispatch(setIsFastActionPage({ isFastActionPage }));
        dispatch(setIsFastBasketPage({ isFastBasketPage }));
        dispatch(setTableContext({ context }));

        if (isMainOperationsEnabled && isFastActionPage) {
            dispatch(clearFilter());
        }
    },[context, dispatch, isFastActionPage, isFastBasketPage, isMainOperationsEnabled]);

    useEffect(() => {
        getListStatus();
    }, [getListStatus]);

    useEffect(() => {
        if (isComplete) {
            setPollingInterval(0);
        }
    }, [isComplete]);

    useEffect(() => {
        if (isComplete && updatedConfig) {
            dispatch(initialise(updatedConfig));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComplete, updatedConfig]);

    useEffect(() => {
        const filterId = new URLSearchParams(location.search).get(TableFilterPresetQueryId);
        if (filterId === SUBSCRIBERS_IN_ROAMING) {
            dispatch(
                setLocalStorageDataAction({
                    key: ELocalStorageKeys.IS_SUBSCRIBERS_IN_ROAMING_PLATE_HIDDEN,
                    value: false,
                }),
            );
        }
    }, [dispatch]);

    // TODO: check extFilter usage

    return (
        <Fragment>
            <PageLocale lazy fileNames={[
                TranslationFileNamesEnum.pageSubscriberMobile,
            ]} />

            <Title pageName={ECommonTranslationPages.subscriberMobile}/>

            <TableSubscriberTitle />
            
            {
                !isComplete && 
                isListStatusFetchedSuccessfully && (
                    <CorporatePagePortal>
                        <SubscriberMobileTableStatusStyled>
                            <InfoBlock
                                className="subscriber-mobile__table-status-info"
                                colorStatus={EColorStatus.WARNING}
                            >
                                {pageTranslation.infoTableUpdate}
                            </InfoBlock>
                        </SubscriberMobileTableStatusStyled>
                    </CorporatePagePortal>
                )
            }
            {
                tableBtnActive && <SubscriberMobileModeStyled>
                    {pageTranslation.buttonMode[ETableButtonsMode.disableAllExceptInArray][tableBtnActive]}
                </SubscriberMobileModeStyled>
            }
            <div {...{ [ONBOARDING_DATA_ATTR]: EOnboardingComponentsId.subscriberTable }}>
                <TableSubscriber />
            </div>
        </Fragment>
    );
};

/* TODO: migrate to rtk. Saga uses for onboarding  */
export default withAsyncStateManagement(memo(PageSubscriberMobile), { sagas: saga });
